import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, TrendingUp, Users, Zap, Award, ArrowRight, Rocket } from 'lucide-react';
import StandardFooter from '../components/StandardFooter';
import ContactModal from '../components/ContactModal';
import '../pages/PortfolioPage.css';

const PortfolioPage = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update page title and meta for SEO
    document.title = 'Portfolio | Offworld Ventures - AI Development Studio';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore the AI-powered solutions and SaaS platforms built by Offworld Ventures. See our portfolio of products solving real-world problems.');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
      // Reset title on unmount
      document.title = 'Offworld Ventures | AI Development Studio';
    };
  }, []);

  const portfolioCompanies = [
    {
      id: 'surfswell',
      name: 'SurfSwell',
      tagline: 'AI-Powered Surf Intelligence & Community',
      description: 'The evolution of surf culture meets cutting-edge AI. SurfSwell combines real-time wave forecasting, community connection, and intelligent surf analytics—inspired by the best of Surfline and Poke, built for the modern surfer.',
      sector: 'AI & Sports Tech',
      founded: '2026',
      website: 'https://surfswell.ai/',
      logo: '/surfswell-logo.png',
      logoType: 'image',
      circleCrop: true,
      metrics: [
        { label: 'Intelligence', value: 'AI-Powered' },
        { label: 'Forecasts', value: 'Real-time' },
        { label: 'Community', value: 'Global' },
        { label: 'Spots', value: '1000+' }
      ],
      highlights: [
        'AI-driven wave and swell forecasting',
        'Community-powered surf spot insights',
        'Real-time conditions and crowd tracking',
        'Personalized surf session recommendations'
      ],
      impact: 'Connecting surfers to the perfect wave. AI meets ocean, community meets stoke.',
      color: '#0EA5E9'
    },
    {
      id: 'urban-oasis',
      name: 'Urban Oasis',
      tagline: 'Tech-Enabled Sanctuaries for Builders',
      description: 'A growing global network of curated retreats designed for founders, entrepreneurs, and builders. Explore, reset, and keep building—whether you\'re escaping burnout or chasing inspiration across continents.',
      sector: 'PropTech & Hospitality',
      founded: '2025',
      website: 'https://urbanoasis.app',
      logo: '/urbanoasis-logo.png',
      logoType: 'image',
      metrics: [
        { label: 'Locations', value: 'Global' },
        { label: 'For', value: 'Builders' },
        { label: 'Experience', value: 'Curated' },
        { label: 'Connectivity', value: '100%' }
      ],
      highlights: [
        'Handpicked sanctuaries in inspiring locations worldwide',
        'High-speed internet and workspace-ready environments',
        'Community of like-minded founders and creators',
        'Seamless booking for digital nomads and remote teams'
      ],
      impact: 'Because the best ideas don\'t always come from an office. Reset. Recharge. Rebuild.',
      color: '#E8956A'
    },
    {
      id: 'smartdaddy',
      name: 'SmartDaddy',
      tagline: 'AI-Powered Family Command Center',
      description: 'Busy parents, meet your AI co-pilot. SmartDaddy intelligently schedules kids\' activities, tracks sports performance, and turns family chaos into organized clarity—so you never miss a game, practice, or proud moment.',
      sector: 'AI & Family Tech',
      founded: '2025',
      website: 'https://smartdaddy.ai',
      logo: '/smartdaddy-logo.png',
      logoType: 'image',
      metrics: [
        { label: 'Scheduling', value: 'AI-Auto' },
        { label: 'Activities', value: 'Unlimited' },
        { label: 'Analytics', value: 'Real-time' },
        { label: 'Family Sync', value: '100%' }
      ],
      highlights: [
        'AI-powered smart scheduling across multiple kids & activities',
        'Sports performance tracking and analytics dashboard',
        'Automatic conflict detection and resolution',
        'Family calendar sync with real-time notifications'
      ],
      impact: 'Giving parents superpowers. Less time juggling schedules, more time cheering from the sidelines.',
      color: '#8B5CF6'
    },
    {
      id: 'bankm8',
      name: 'BankM8',
      tagline: 'AI-Powered Bank Statement Intelligence',
      description: 'Transform chaotic PDF bank statements into actionable insights. Convert to CSV, XLSX, or JSON in seconds. Merge statements, visualize spending patterns, and let AI analyze your finances—without ever training on your data.',
      sector: 'Fintech & AI',
      founded: '2025',
      website: 'https://bankm8.app/',
      logo: '/bankm8-logo.png',
      logoType: 'image',
      metrics: [
        { label: 'Export Formats', value: '4+' },
        { label: 'Privacy', value: '100%' },
        { label: 'Processing', value: 'Instant' },
        { label: 'AI Training', value: 'Zero' }
      ],
      highlights: [
        'PDF to CSV, XLSX, JSON conversion in seconds',
        'Merge multiple statements for complete financial picture',
        'AI-powered spending analysis and insights',
        'Privacy-first: Your data is never used for training'
      ],
      impact: 'Financial clarity without compromise. Your bank data stays yours—we just make it usable.',
      color: '#3B82F6'
    },
    {
      id: 'marfi-launchpad',
      name: 'MARFI LAUNCHPAD',
      tagline: 'AI-Driven Cybersecurity & IT Platform for SMBs',
      description: 'Enterprise-grade security without enterprise complexity. MARFI LAUNCHPAD automates IT management, compliance, and cybersecurity—helping SMBs achieve SOC 2, ISO 27001, and HIPAA 50% faster while defending against the $11 trillion cybercrime epidemic.',
      sector: 'Cybersecurity & AI',
      founded: '2020',
      website: 'https://marfi.io',
      logo: '/marfi-logo.jpg',
      logoType: 'image',
      metrics: [
        { label: 'Compliance Speed', value: '50% Faster' },
        { label: 'Frameworks', value: '28+' },
        { label: 'Attack Prevention', value: '95%' },
        { label: 'Team Size', value: '1-1000+' }
      ],
      highlights: [
        'AI-powered threat detection and automated remediation',
        'SOC 2, ISO 27001, HIPAA compliance automation',
        'Real-time network monitoring and DDoS protection',
        'US-based support with white-glove implementation'
      ],
      impact: 'Democratizing enterprise security for growing businesses. 95% of cyberattacks are preventable—MARFI makes prevention automatic.',
      color: '#DC3545'
    },
    {
      id: 'edgeiq',
      name: 'edgeIQ',
      tagline: 'AI-Powered Training & Quiz Platform for Cloudflare',
      description: 'Master Cloudflare with AI-powered training, interactive quizzes, and RAG-enhanced assistance. Navigate how-tos, documentation, and Cloudflare knowledge with intelligent AI guidance.',
      sector: 'AI & EdTech',
      founded: '2025',
      website: 'https://edgeiq.live',
      logo: '/edgeiq-logo.png',
      logoType: 'image',
      metrics: [
        { label: 'AI Model', value: 'RAG' },
        { label: 'Knowledge Base', value: 'Cloudflare' },
        { label: 'Learning Mode', value: 'Interactive' },
        { label: 'Assistance', value: '24/7 AI' }
      ],
      highlights: [
        'RAG-enhanced AI for accurate Cloudflare guidance',
        'Interactive quizzes for hands-on learning',
        'Comprehensive how-to documentation navigation',
        'Real-time AI assistance for technical questions'
      ],
      impact: 'Making Cloudflare expertise accessible to everyone through AI-powered learning and intelligent documentation assistance.',
      color: '#F6821F'
    },
    {
      id: 'franchise-design-studio',
      name: 'Franchise Design Studio',
      tagline: 'AI-Powered Digital Solutions for Franchises',
      description: 'Transform franchises with AI-powered digital solutions. Custom websites, apps, analytics dashboards, and marketing automation built in 3-14 days.',
      sector: 'AI & SaaS',
      founded: '2024',
      website: 'https://franchisedesignstudio.com/',
      logo: '/fds-logo.png',
      logoType: 'image',
      metrics: [
        { label: 'Delivery Time', value: '3-7 Days' },
        { label: 'Franchise Locations', value: '200+' },
        { label: 'Solutions', value: '6+' },
        { label: 'Certified', value: 'SOC2' }
      ],
      highlights: [
        'AI-powered development platform',
        'Enterprise-grade security (SOC2 certified)',
        '100% US/EU based teams',
        'Lightning-fast delivery in days'
      ],
      impact: 'Revolutionizing how franchises deploy digital infrastructure with AI acceleration and expert teams.',
      color: '#D4A574'
    }
  ];

  return (
    <div className="portfolio-page">
      {/* Fixed Header */}
      <header className="portfolio-header">
        <div className="portfolio-header-content">
          <Link to="/" className="back-link">
            <img src="/logo.png" alt="Offworld Ventures" className="header-logo" />
            <span className="header-logo-text">OFFWORLD VENTURES</span>
          </Link>
          <Link to="/" className="nav-home-link">
            Back to Home
          </Link>
        </div>
      </header>

      {/* Space Background */}
      <div className="portfolio-space-bg">
        <div className="stars-layer stars-small"></div>
        <div className="stars-layer stars-medium"></div>
        <div className="portfolio-nebula"></div>
      </div>

      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="hero-decoration">
          <div className="orbit-decoration orbit-1"></div>
          <div className="orbit-decoration orbit-2"></div>
        </div>
        <div className="portfolio-hero-content">
          <div className="hero-badge">
            <Rocket size={24} />
            <span>WHAT WE BUILD</span>
          </div>
          <h1 className="portfolio-hero-title">
            What We&apos;ve <span className="gold-text">Built</span>
          </h1>
          <p className="portfolio-hero-subtitle">
            AI-powered products we built, own, and operate—plus the capabilities we bring to every client engagement. Born from obsession with solving real problems.
          </p>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="stat-number">{portfolioCompanies.length}</div>
              <div className="stat-label">Active Products</div>
            </div>
            <div className="stat-divider"></div>
            <div className="hero-stat">
              <div className="stat-number">AI</div>
              <div className="stat-label">First Approach</div>
            </div>
            <div className="stat-divider"></div>
            <div className="hero-stat">
              <div className="stat-number">100%</div>
              <div className="stat-label">In-House Built</div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Companies */}
      <section className="portfolio-companies">
        <div className="portfolio-container">
          <div className="portfolio-section-label">
            <h2 className="portfolio-section-title">Our <span className="gold-text">Products</span></h2>
            <p className="portfolio-section-subtitle">Platforms we built, own, and operate.</p>
          </div>
          {portfolioCompanies.map((company, index) => (
            <div
              key={company.id}
              id={company.id}
              className={`company-card ${isVisible[company.id] ? 'visible' : ''}`}
              data-animate
            >
              {/* Company Header */}
              <div className="company-header">
                <div className="company-logo-section">
                  <div className="company-logo" style={{ borderColor: company.color }}>
                    {company.logoType === 'image' ? (
                      <img src={company.logo} alt={`${company.name} logo`} className={`logo-image${company.circleCrop ? ' circle-crop' : ''}`} />
                    ) : (
                      <span className="logo-emoji">{company.logo}</span>
                    )}
                  </div>
                  <div className="company-title-group">
                    <h2 className="company-name">{company.name}</h2>
                    <p className="company-tagline">{company.tagline}</p>
                  </div>
                </div>
                <a 
                  href={company.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="visit-button"
                  style={{ borderColor: company.color }}
                >
                  <span>Visit Site</span>
                  <ExternalLink size={18} />
                </a>
              </div>

              {/* Company Info Grid */}
              <div className="company-info-grid">
                <div className="info-item">
                  <TrendingUp size={20} style={{ color: company.color }} />
                  <span className="info-label">Sector</span>
                  <span className="info-value">{company.sector}</span>
                </div>
                <div className="info-item">
                  <Award size={20} style={{ color: company.color }} />
                  <span className="info-label">Founded</span>
                  <span className="info-value">{company.founded}</span>
                </div>
              </div>

              {/* Description */}
              <div className="company-description">
                <p>{company.description}</p>
              </div>

              {/* Metrics Grid */}
              <div className="metrics-grid">
                {company.metrics.map((metric, idx) => (
                  <div key={idx} className="metric-card">
                    <div className="metric-value" style={{ color: company.color }}>
                      {metric.value}
                    </div>
                    <div className="metric-label">{metric.label}</div>
                  </div>
                ))}
              </div>

              {/* Highlights */}
              <div className="highlights-section">
                <h3 className="highlights-title">Key Highlights</h3>
                <ul className="highlights-list">
                  {company.highlights.map((highlight, idx) => (
                    <li key={idx} className="highlight-item">
                      <Zap size={16} style={{ color: company.color }} />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Impact Statement */}
              <div className="impact-section">
                <div className="impact-icon">
                  <Rocket size={24} style={{ color: company.color }} />
                </div>
                <div className="impact-content">
                  <h4 className="impact-title">Why We Built This</h4>
                  <p className="impact-text">{company.impact}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Client Capabilities */}
      <section className="client-capabilities-section">
        <div className="portfolio-container">
          <div className="portfolio-section-label">
            <h2 className="portfolio-section-title">Client <span className="gold-text">Capabilities</span></h2>
            <p className="portfolio-section-subtitle">Everything we build for ourselves, we build for you too.</p>
          </div>

          <div className="capabilities-grid">
            <div className="capability-card">
              <h3 className="capability-title">Web Applications</h3>
              <p className="capability-text">Full-stack web apps with AI integrations, from internal tools to customer-facing platforms.</p>
            </div>
            <div className="capability-card">
              <h3 className="capability-title">Mobile Apps</h3>
              <p className="capability-text">Cross-platform mobile applications with native performance and AI-powered features.</p>
            </div>
            <div className="capability-card">
              <h3 className="capability-title">AI Agents & Chatbots</h3>
              <p className="capability-text">Custom AI agents, conversational interfaces, and intelligent automation workflows.</p>
            </div>
            <div className="capability-card">
              <h3 className="capability-title">SaaS Development</h3>
              <p className="capability-text">End-to-end SaaS products—architecture, billing, auth, deployment, and growth.</p>
            </div>
            <div className="capability-card">
              <h3 className="capability-title">API Design & Integration</h3>
              <p className="capability-text">RESTful and GraphQL APIs, third-party integrations, and data pipeline architecture.</p>
            </div>
            <div className="capability-card">
              <h3 className="capability-title">AI Feature Integration</h3>
              <p className="capability-text">Embed AI capabilities into your existing products—search, recommendations, automation, analysis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="portfolio-cta">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Build Something?</h2>
          <p className="cta-text">
            Whether you need an app, a platform, an AI tool, or something that doesn&apos;t exist yet—tell us what you&apos;re thinking. We&apos;ll tell you how fast we can ship it.
          </p>
          <button 
            className="cta-button"
            onClick={() => setIsContactOpen(true)}
          >
            <span>Start the Conversation</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <StandardFooter onContactClick={() => setIsContactOpen(true)} />

      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
};

export default PortfolioPage;
